import React from 'react'
import { SearchState } from './types';
import { Input } from '../../../ui/input';
import { Button } from '../../../ui/button';
import { Checkbox } from '../../../ui/checkbox';
import { Label } from '../../../ui/label';
import { DateInput } from '../../../ui/date-input';
import { MultiSelect } from '../../../ui/multi-select';
import * as Icon from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import fitdocApi from 'src/lib/axios';
import { ReactSession }  from 'react-client-session';
import { getDocomsTrans } from 'src/lib/trans';
import { ContentLabel } from '../../../helpers/content-label'
import { useSelector } from 'react-redux';

type Props = {
    onSearch: () => void;
    isPending: boolean;
    
    hideDoctypes?: boolean;
    hideSearchContent?: boolean;
    hideRelations?: boolean;

    labels?: {
        title?: string;
        description?: string;
        search_content?: string;
        search_button?: string;
    }
}

const Filter = ({onSearch, isPending, hideDoctypes=false, hideSearchContent=false, hideRelations=true, labels={}}:Props) => {
    const relations = useSelector((state:any)=>state.allRelations);
    const language = ReactSession.get("language");
    const form = useFormContext<SearchState>()

    const { data:doctypes } = useQuery({
        queryKey: ['doctypes'],
        queryFn: async () => {
            const {data:dt} = await fitdocApi.get('doctypes/list')
            if(dt.errorcode!==0)  throw new Error('Error fetching doctypes');
            if(!Array.isArray(dt.object)) throw new Error('Error fetching doctypes');
            return dt.object;
        },
        enabled: hideDoctypes ? false : true
    })

    const doctypeOptions = Array.isArray(doctypes) ? doctypes?.map((d:any) => ({value:d.doctype, label:getDocomsTrans(d?.docoms, language)})) || [] : []
    const relationOptions = Array.isArray(relations) ? relations?.map((r:any) => ({value:r.split("_")[0], label:r?.replace('_', ' ')})) || [] : []

  return (
    <div className='p-4 space-y-4 border rounded-lg md:p-8'>
        <div>
            <ContentLabel path={labels.title} className='text-xl font-semibold'/>
            <ContentLabel path={labels.description} />
        </div>

        <div className='space-y-2'>
            <div className='flex flex-col gap-1'>
                <Input {...form.register('searchstring')} startEnhancer={<Icon.Search className='w-4 h-4 text-neutral-400'/>} />
                {!hideSearchContent && 
                <div className='flex items-center justify-end'>
                    <div className='flex items-center gap-1'>
                        <Controller 
                            control={form.control}
                            name='searchcontent'
                            render={({ field }) => (
                                <Checkbox checked={field.value} onCheckedChange={field.onChange} id='searchcontent'/>
                            )}
                        />
                        <Label htmlFor='searchcontent'>
                            <ContentLabel path={labels.search_content} />
                        </Label>
                    </div>
                </div>
                }
            </div>

            <div className='flex items-center gap-2'>
                <Controller 
                    control={form.control}
                    name='startdate'
                    render={({ field }) => (
                        <DateInput date={field.value} onDateChange={field.onChange}/>
                    )}
                />
                <span><Icon.ArrowRight className='w-4 h-4 text-neutral-400'/></span>
                <Controller 
                    control={form.control}
                    name='enddate'
                    render={({ field }) => (
                        <DateInput date={field.value} onDateChange={field.onChange}/>
                    )}
                />
            </div>

            {!hideDoctypes && 
            <div>
                <Controller 
                    control={form.control}
                    name='doctypes'
                    render={({ field }) => (
                        <MultiSelect 
                            options={doctypeOptions} 
                            placeholder='Type...' 
                            onValueChange={(v)=>{ field.onChange(v)}}
                            defaultValue={field.value}
                        />
                    )}
                />
            </div>
            }

            {!hideRelations &&
            <div>
            <Controller 
                control={form.control}
                name='relations'
                render={({ field }) => (
                    <MultiSelect 
                        options={relationOptions} 
                        placeholder='Relation...' 
                        onValueChange={(v)=>{ field.onChange(v)}}
                        defaultValue={field.value}
                        longList
                    />
                )}
            />
        </div>
            }
        </div>

        <Button
            onClick={()=>{onSearch()}}
            disabled={isPending}
        >
            {isPending && <Icon.Loader2 className='w-4 h-4 animate-spin'/>}
            <ContentLabel path={labels.search_button} />
        </Button>
    </div>
  )
}

export default Filter